import React, { useEffect, useState } from 'react';
import PersonOffIcon from '@mui/icons-material/PersonOff';

import { useI18n } from '../../hooks/useI18n';
import {
  CardsGrid,
  NoContactsIcon,
  NoContactsMessage,
  NoContactsWrapper,
  SkeletonAvatar,
  SkeletonCardContainer,
  SkeletonText,
} from './styles';
import { TranslationKeys } from '../../i18n/locales/defaultResources';
import ConsultantCardInfo from '../../presentation/features/contacts/ConsultantCardInfo/ConsultantCardInfo';
import { useBpStore } from '../../stores/bpStore';
import { useContextStore } from '../../stores/contextStore';
import PageHeader from '../../presentation/layout/PageHeader/PageHeader';
import { MainContainer } from '../../styles/styles';
import SecurityTrim from '../../services/auth/SecurityTrim';
import { CropConnectRoleDefinitions } from '../../services/cropConnectApiClient';

const ConsultantCardSkeleton = () => (
  <SkeletonCardContainer>
    <SkeletonAvatar
      variant="circular"
      width={120}
      height={120}
      animation="wave"
    />
    <SkeletonText variant="text" width={100} height={24} animation="wave" />
    <SkeletonText variant="text" width={150} height={20} animation="wave" />
    <SkeletonText
      variant="rectangular"
      width="100%"
      height={40}
      animation="wave"
      style={{ borderRadius: '8px' }}
    />
    <SkeletonText
      variant="rectangular"
      width="100%"
      height={40}
      animation="wave"
      style={{ borderRadius: '8px' }}
    />
  </SkeletonCardContainer>
);

const MyContacts = () => {
  const {
    consultants,
    isLoading,
    fetchConsultantsByFactory,
    fetchBusinessPartnerMetadata,
  } = useBpStore();
  const { activeBpNumber } = useContextStore();
  const [localConsultants, setLocalConsultants] = useState(consultants);
  const { t } = useI18n();

  useEffect(() => {
    setLocalConsultants(null);

    const fetchData = async () => {
      await fetchBusinessPartnerMetadata();
      await fetchConsultantsByFactory();
    };

    fetchData();
  }, [activeBpNumber, fetchBusinessPartnerMetadata, fetchConsultantsByFactory]);

  useEffect(() => {
    if (consultants) {
      setLocalConsultants(consultants);
    }
  }, [consultants]);

  const handlePhoneClick = (phone?: string) => {
    if (phone) {
      window.open(`tel:${phone}`, '_self');
    }
  };

  const handleEmailClick = (email?: string) => {
    if (email) {
      window.open(`mailto:${email}`, '_self');
    }
  };

  return (
    <MainContainer>
      <PageHeader title={t(TranslationKeys.my_contacts_title)} />

      <SecurityTrim
        requiredBusinessPartnerRoles={[CropConnectRoleDefinitions.Grower]}
      >
        {isLoading || !localConsultants ? (
          <CardsGrid>
            {Array.from({ length: 6 }).map((_, index) => (
              <ConsultantCardSkeleton key={index} />
            ))}
          </CardsGrid>
        ) : (
          <CardsGrid>
            {localConsultants?.consultants?.length ? (
              localConsultants.consultants.map((consultant, index) => (
                <ConsultantCardInfo
                  key={index}
                  displayName={consultant.displayName}
                  description={consultant.description}
                  phone={consultant.phone}
                  mobile={consultant.mobile}
                  email={consultant.email}
                  image={consultant.image}
                  onPhoneClick={handlePhoneClick}
                  onEmailClick={handleEmailClick}
                />
              ))
            ) : (
              <NoContactsWrapper>
                <NoContactsIcon>
                  <PersonOffIcon />
                </NoContactsIcon>
                <NoContactsMessage>
                  {t(TranslationKeys.my_contacts_no_contacts_found)}
                </NoContactsMessage>
              </NoContactsWrapper>
            )}
          </CardsGrid>
        )}
      </SecurityTrim>
    </MainContainer>
  );
};

export default MyContacts;
